import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Plus, ArrowLeft, Save, Trash2, Search, FileText,
  DollarSign, CheckCircle, Clock, AlertCircle, Printer, Eye, ExternalLink, StickyNote
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = v => v != null ? `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00';

const STATUSES = ['Draft', 'Pending', 'Approved', 'Paid', 'Closed'];
const STATUS_COLORS = {
  Draft: 'bg-gray-100 text-gray-700',
  Pending: 'bg-amber-100 text-amber-700',
  Approved: 'bg-blue-100 text-blue-700',
  Paid: 'bg-emerald-100 text-emerald-700',
  Closed: 'bg-slate-200 text-slate-600',
};

const CInput = ({ label, value, onChange, testId, readOnly }) => {
  const [raw, setRaw] = useState('');
  const [focused, setFocused] = useState(false);
  const display = value !== '' && value != null && value !== 0
    ? Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
  return (
    <div>
      {label && <Label className="text-xs text-slate-500">{label}</Label>}
      <div className="relative">
        <span className="absolute left-2.5 top-2 text-gray-400 text-sm">$</span>
        <input type="text" data-testid={testId} readOnly={readOnly}
          value={focused ? raw : display}
          onFocus={() => { if (readOnly) return; setFocused(true); setRaw(value && value !== 0 ? String(value) : ''); }}
          onBlur={() => setFocused(false)}
          onChange={e => { if (readOnly) return; const v = e.target.value.replace(/[^0-9.,]/g, ''); setRaw(v); onChange(parseFloat(v.replace(/,/g, '')) || 0); }}
          placeholder="0.00" className={`w-full pl-7 pr-3 py-2 border rounded-lg text-sm ${readOnly ? 'bg-gray-50 text-slate-600' : ''}`} />
      </div>
    </div>
  );
};

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <Label className="text-xs text-slate-500">{label}</Label>
    {children}
  </div>
);

const TInput = ({ label, value, onChange, type = 'text', placeholder = '', testId, className = '', readOnly }) => (
  <Field label={label} className={className}>
    <input type={type} data-testid={testId} value={value || ''} readOnly={readOnly}
      onChange={e => !readOnly && onChange(e.target.value)}
      placeholder={placeholder} className={`w-full px-3 py-2 border rounded-lg text-sm ${readOnly ? 'bg-gray-50 text-slate-600' : ''}`} />
  </Field>
);

function fileUrl(servePath) { return `${API}/api${encodeURI(servePath)}`; }

// ═══════════════════════════════════════════
// COVERSHEET FORM (Create / Edit)
// ═══════════════════════════════════════════
const CoversheetForm = ({ projectId, coversheetId, onBack, project, projectContext, readOnly = false }) => {
  const projectName = project?.name || '';

  const buildAutoForm = () => {
    const fin = projectContext?.fin || {};
    const subs = projectContext?.subs || [];
    const customerInfo = projectContext?.customerInfo || {};
    const invoices = projectContext?.invoices || [];
    const docs = projectContext?.docs || {};

    const vendorPayables = subs.map(s => ({
      assignment_id: s.id || '',
      subcontractor_id: s.subcontractor_id || '',
      name: s.sub_info?.company_name || 'Unknown',
      trade: s.sub_info?.trade || '',
      bcs_po_number: s.bcs_po_number || '',
      payable_amount: s.committed_cost || s.contract_amount || 0,
    }));

    // Get BCS Invoice # from the latest invoice
    const latestInvoice = invoices.length > 0 ? invoices[0] : null;
    const bcsInvNum = latestInvoice?.invoice_number || '';

    // Pull the Customer PO number — prefer the most recent uploaded PO (from `customer_pos` array)
    const cpoArr = project?.customer_pos || [];
    const customerPo = cpoArr.length > 0 ? cpoArr[cpoArr.length - 1] : project?.customer_po;
    const customerPoNumber = customerPo?.po_number || customerPo?.filename?.replace(/\.[^/.]+$/, '') || '';

    return {
      customer_name: customerInfo.customer_name || project?.customer_company || '',
      customer_address: customerInfo.customer_address || '',
      customer_city_state_zip: '',
      customer_phone: customerInfo.customer_phone || '',
      customer_email: customerInfo.customer_email || '',
      bcs_invoice_number: bcsInvNum,
      invoice_date: new Date().toISOString().split('T')[0],
      customer_po_number: customerPoNumber,
      work_order_number: project?.project_number || '',
      service_date_from: '', service_date_to: '',
      terms: 'Net 30',
      description_of_work: '',
      total_receivable: fin.revised_contract_value || project?.original_contract_value || 0,
      vendor_payables: vendorPayables,
      misc_payable: 0, misc_payable_description: '',
      status: 'Draft',
      attachments_notes: '',
      attachment_notes: [],
      quickbooks_invoice_number: '', quickbooks_synced: false,
      notes: [{ text: '', created_at: new Date().toISOString() }],
      internal_notes: project?.project_number ? `Project #: ${project.project_number}\nProject: ${project.name}` : '',
    };
  };

  const emptyForm = {
    customer_name: '', customer_address: '', customer_city_state_zip: '',
    customer_phone: '', customer_email: '', bcs_invoice_number: '', invoice_date: '',
    customer_po_number: '', work_order_number: '', service_date_from: '', service_date_to: '',
    terms: 'Net 30', description_of_work: '',
    total_receivable: 0, vendor_payables: [], misc_payable: 0, misc_payable_description: '',
    status: 'Draft', attachments_notes: '',
    attachment_notes: [],
    quickbooks_invoice_number: '', quickbooks_synced: false,
    notes: [], internal_notes: '',
  };

  const [form, setForm] = useState(() => coversheetId ? { ...emptyForm } : buildAutoForm());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!coversheetId);
  const printRef = useRef(null);

  useEffect(() => {
    if (!coversheetId) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/crm/projects/${projectId}/coversheets/${coversheetId}`, { credentials: 'include' });
        const data = await res.json();
        setForm(f => {
          const merged = { ...f };
          Object.keys(f).forEach(k => { if (data[k] !== undefined) merged[k] = data[k]; });
          // Backward compat: convert old fields
          if ((!data.vendor_payables || data.vendor_payables.length === 0) && (data.mako_payable || data.beica_payable)) {
            const legacy = [];
            if (data.mako_payable) legacy.push({ name: 'Mako', trade: '', bcs_po_number: '', payable_amount: data.mako_payable });
            if (data.beica_payable) legacy.push({ name: 'BeICA', trade: '', bcs_po_number: '', payable_amount: data.beica_payable });
            merged.vendor_payables = legacy;
          }
          if (data.invoice_number && !data.bcs_invoice_number) merged.bcs_invoice_number = data.invoice_number;
          if (data.po_number && !data.customer_po_number) merged.customer_po_number = data.po_number;
          if (!merged.notes || !Array.isArray(merged.notes)) merged.notes = [];
          if (!merged.attachment_notes) merged.attachment_notes = [];
          return merged;
        });
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    })();
  }, [coversheetId, projectId]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const vendorTotal = (form.vendor_payables || []).reduce((s, vp) => s + (parseFloat(vp.payable_amount) || 0), 0);
  const netProfit = (form.total_receivable || 0) - vendorTotal - (form.misc_payable || 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, net_profit: netProfit };
      const url = coversheetId
        ? `${API}/api/crm/projects/${projectId}/coversheets/${coversheetId}`
        : `${API}/api/crm/projects/${projectId}/coversheets`;
      const res = await fetch(url, {
        method: coversheetId ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success(coversheetId ? 'Coversheet updated' : 'Coversheet created');
      onBack();
    } catch (e) { toast.error(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const BCS_LOGO_URL = "https://customer-assets.emergentagent.com/job_a4932d93-1a08-4aba-97f8-fc665534a903/artifacts/e6h6bxv7_BF726A6E-A369-4D1E-89C3-313F853560BC_1_105_c.jpeg";

  const handlePrint = () => {
    const vps = form.vendor_payables || [];
    const vpRows = vps.map(vp =>
      `<tr><td>${vp.name || ''}${vp.trade ? ' (' + vp.trade + ')' : ''}${vp.bcs_po_number ? ' — BCS PO: ' + vp.bcs_po_number : ''}</td><td style="text-align:right; color:#dc2626;">($${Number(vp.payable_amount||0).toLocaleString(undefined,{minimumFractionDigits:2})})</td></tr>`
    ).join('');
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>BCS Invoice Coversheet - ${form.bcs_invoice_number || 'Draft'}</title>
      <style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:'IBM Plex Sans',Arial,sans-serif;font-size:11px;color:#1a1a1a;padding:24px;}h1{font-size:20px;color:#0b2545;margin-bottom:2px;letter-spacing:-0.3px;}h2{font-size:13px;font-weight:700;color:#0b2545;border-bottom:2px solid #f15339;padding-bottom:3px;margin:16px 0 8px;}.grid{display:grid;gap:6px 16px;}.g2{grid-template-columns:1fr 1fr;}.g3{grid-template-columns:1fr 1fr 1fr;}.g4{grid-template-columns:1fr 1fr 1fr 1fr;}.field label{font-size:9px;text-transform:uppercase;color:#666;display:block;letter-spacing:0.5px;}.field span{font-size:12px;font-weight:500;}.profit-box{background:#f0fdf4;border:1px solid #86efac;border-radius:6px;padding:12px;text-align:center;margin-top:12px;}.profit-box .label{font-size:10px;color:#166534;text-transform:uppercase;}.profit-box .value{font-size:22px;font-weight:800;color:#166534;}.status{display:inline-block;padding:3px 10px;border-radius:12px;font-size:10px;font-weight:600;background:#dbeafe;color:#1e40af;letter-spacing:0.5px;}table{width:100%;border-collapse:collapse;margin:6px 0;}td,th{padding:4px 8px;font-size:11px;border-bottom:1px solid #e5e7eb;}th{text-align:left;font-size:9px;text-transform:uppercase;color:#666;background:#f8fafc;}.bcs-shield{width:64px;height:64px;border-radius:50%;object-fit:cover;border:2px solid #0b2545;}.header{display:flex;justify-content:space-between;align-items:center;padding-bottom:14px;border-bottom:3px solid #0b2545;margin-bottom:14px;}@media print{body{padding:12px;}.header{page-break-after:avoid;}}</style></head><body>
      <div class="header">
        <div style="display:flex;align-items:center;gap:14px;">
          <img src="${BCS_LOGO_URL}" alt="BCS Shield" class="bcs-shield" />
          <div>
            <h1>BCS Invoice Coversheet</h1>
            <p style="font-size:10px;color:#475569;letter-spacing:0.5px;">BCS INDUSTRIAL SERVICES · Heavy Civil &amp; Industrial Contractor</p>
            <p style="font-size:10px;color:#666;margin-top:2px;">Project: <strong>${projectName||'—'}</strong>${project?.project_number ? ' &nbsp;·&nbsp; #' + project.project_number : ''}</p>
          </div>
        </div>
        <div style="text-align:right">
          <span class="status">${form.status}</span>
          <p style="font-size:10px;color:#666;margin-top:6px;">Invoice Date: <strong>${form.invoice_date||'—'}</strong></p>
          <p style="font-size:11px;color:#0b2545;font-weight:700;margin-top:2px;">${form.bcs_invoice_number ? 'BCS Inv #' + form.bcs_invoice_number : ''}</p>
        </div>
      </div>

      <h2>Bill To</h2>
      <div class="grid g2">
        <div class="field"><label>Customer</label><span>${form.customer_name||'—'}</span></div>
        <div class="field"><label>Phone</label><span>${form.customer_phone||'—'}</span></div>
        <div class="field"><label>Address</label><span>${form.customer_address||'—'}${form.customer_city_state_zip ? ', ' + form.customer_city_state_zip : ''}</span></div>
        <div class="field"><label>Email</label><span>${form.customer_email||'—'}</span></div>
      </div>

      <h2>Invoice References</h2>
      <div class="grid g4">
        <div class="field"><label>Customer PO #</label><span>${form.customer_po_number||'—'}</span></div>
        <div class="field"><label>Work Order #</label><span>${form.work_order_number||'—'}</span></div>
        <div class="field"><label>Service Dates</label><span>${form.service_date_from||'—'}${form.service_date_to ? ' – ' + form.service_date_to : ''}</span></div>
        <div class="field"><label>Terms</label><span>${form.terms||'Net 30'}</span></div>
      </div>

      ${form.description_of_work ? `<h2>Description of Work</h2><p style="font-size:11px;white-space:pre-wrap;line-height:1.5;">${form.description_of_work}</p>` : ''}

      <h2>Subcontractors / Vendors</h2>
      <table><tr><th>Vendor</th><th>Trade</th><th>BCS PO #</th><th style="text-align:right;">Amount</th></tr>
      ${vps.length === 0 ? '<tr><td colspan="4" style="color:#94a3b8;text-align:center;">No vendors assigned</td></tr>' :
        vps.map(vp=>`<tr><td>${vp.name||''}</td><td>${vp.trade||''}</td><td style="font-family:monospace;font-size:10px;">${vp.bcs_po_number||'—'}</td><td style="text-align:right">$${Number(vp.payable_amount||0).toLocaleString(undefined,{minimumFractionDigits:2})}</td></tr>`).join('')}
      </table>

      <h2>Financials</h2>
      <table>
        <tr><td><strong>Total Receivable from Customer</strong></td><td style="text-align:right;font-weight:700;color:#166534;">$${Number(form.total_receivable||0).toLocaleString(undefined,{minimumFractionDigits:2})}</td></tr>
        ${vpRows}
        ${form.misc_payable>0?`<tr><td>Misc ${form.misc_payable_description?'('+form.misc_payable_description+')':''}</td><td style="text-align:right;color:#dc2626;">($${Number(form.misc_payable||0).toLocaleString(undefined,{minimumFractionDigits:2})})</td></tr>`:''}
      </table>
      <div class="profit-box"><div class="label">Net Profit</div><div class="value">$${Number(netProfit).toLocaleString(undefined,{minimumFractionDigits:2})}</div></div>
      ${(form.notes||[]).length>0 && form.notes.some(n=>n.text)?`<h2>Notes</h2>${form.notes.filter(n=>n.text).map(n=>`<p style="font-size:11px;white-space:pre-wrap;margin-bottom:4px;line-height:1.5;">${n.text||''}</p>`).join('')}`:''}

      <div style="margin-top:24px;padding-top:8px;border-top:1px solid #e5e7eb;font-size:9px;color:#94a3b8;text-align:center;letter-spacing:0.5px;">
        BCS INDUSTRIAL SERVICES &nbsp;·&nbsp; bcs-us.com &nbsp;·&nbsp; Generated ${new Date().toLocaleString()}
      </div>
      </body></html>`);
    win.document.close();
    setTimeout(() => { win.print(); }, 500);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>;

  const vps = form.vendor_payables || [];
  const docs = projectContext?.docs || {};
  const allDocs = [...(docs.sub_quotes||[]), ...(docs.vendor_invoices||[]), ...(docs.paid_invoices||[])];
  if (docs.bcs_quote) allDocs.unshift(docs.bcs_quote);
  if (docs.customer_po) allDocs.push(docs.customer_po);

  return (
    <div className="space-y-4" ref={printRef}>
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="cs-back-btn"><ArrowLeft size={16} className="mr-1" /> Back to List</Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-coversheet"><Printer size={14} className="mr-1" /> Export PDF</Button>
          {!readOnly && (
            <Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={handleSave} disabled={saving} data-testid="save-coversheet">
              <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </div>
      </div>

      {readOnly && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm text-blue-800 flex items-center gap-2">
          <Eye size={14} /> View-Only Mode
        </div>
      )}

      {/* Status Step Bar */}
      <Card><CardContent className="pt-4 pb-3">
        <div className="flex items-center justify-between">
          {STATUSES.map((s, i) => {
            const active = form.status === s;
            const done = STATUSES.indexOf(form.status) > i;
            return (
              <React.Fragment key={s}>
                {i > 0 && <div className={`flex-1 h-0.5 mx-1 ${done ? 'bg-blue-600' : 'bg-gray-200'}`} />}
                <button onClick={() => !readOnly && set('status', s)} data-testid={`status-${s.toLowerCase()}`}
                  disabled={readOnly}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors
                    ${active ? 'bg-blue-900 text-white' : done ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}
                    ${readOnly ? 'cursor-default' : ''}`}>
                  {done ? <CheckCircle size={12} /> : active ? <Clock size={12} /> : <span className="w-3 h-3 rounded-full border border-current inline-block" />}
                  {s}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </CardContent></Card>

      {/* Section 1: Customer & Invoice Info */}
      <Card><CardContent className="pt-5">
        <h3 className="text-xs font-semibold text-blue-800 uppercase tracking-wider mb-3">Customer & Invoice Info</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <TInput label="Customer Name" value={form.customer_name} onChange={v => set('customer_name', v)} testId="cs-customer" readOnly={readOnly} />
          <TInput label="BCS Invoice #" value={form.bcs_invoice_number} onChange={v => set('bcs_invoice_number', v)} testId="cs-inv-number" readOnly={readOnly} />
          <TInput label="Invoice Date" type="date" value={form.invoice_date} onChange={v => set('invoice_date', v)} testId="cs-inv-date" readOnly={readOnly} />
          <TInput label="Phone" value={form.customer_phone} onChange={v => set('customer_phone', v)} testId="cs-customer-phone" readOnly={readOnly} />
          <TInput label="Address" value={form.customer_address} onChange={v => set('customer_address', v)} className="md:col-span-2" testId="cs-customer-address" readOnly={readOnly} />
          <TInput label="City / State / Zip" value={form.customer_city_state_zip} onChange={v => set('customer_city_state_zip', v)} readOnly={readOnly} />
          <TInput label="Email" value={form.customer_email} onChange={v => set('customer_email', v)} readOnly={readOnly} />
        </div>
      </CardContent></Card>

      {/* Section 2: Subcontractors / Vendors (auto-populated from Subcontractors tab) */}
      <Card><CardContent className="pt-5">
        <h3 className="text-xs font-semibold text-blue-800 uppercase tracking-wider mb-3">Subcontractors / Vendors & Purchase Orders</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <TInput label="Customer PO #" value={form.customer_po_number} onChange={v => set('customer_po_number', v)} testId="cs-customer-po" readOnly={readOnly} />
          <TInput label="Work Order #" value={form.work_order_number} onChange={v => set('work_order_number', v)} testId="cs-work-order" readOnly={readOnly} />
          <TInput label="Service Date From" type="date" value={form.service_date_from} onChange={v => set('service_date_from', v)} readOnly={readOnly} />
          <TInput label="Service Date To" type="date" value={form.service_date_to} onChange={v => set('service_date_to', v)} readOnly={readOnly} />
          <TInput label="Terms" value={form.terms} onChange={v => set('terms', v)} placeholder="Net 30" readOnly={readOnly} />
        </div>
        {vps.length > 0 ? (
          <div className="bg-slate-50 rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Vendor / Sub</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Trade</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">BCS PO #</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-slate-600">Contract Amount</th>
                </tr>
              </thead>
              <tbody>
                {vps.map((vp, idx) => (
                  <tr key={idx} className="border-t border-slate-200">
                    <td className="px-3 py-2 font-medium text-slate-800">{vp.name}</td>
                    <td className="px-3 py-2 text-slate-500">{vp.trade || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{vp.bcs_po_number || '—'}</td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-700">{fmt(vp.payable_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-400">No vendors / subcontractors assigned. Add them in the Vendors / Subs tab.</p>
        )}
        <div className="mt-3">
          <Label className="text-xs text-slate-500">Description of Work</Label>
          <textarea value={form.description_of_work || ''} disabled={readOnly}
            onChange={e => set('description_of_work', e.target.value)}
            rows={3} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Describe the work performed..." />
        </div>
      </CardContent></Card>

      {/* Section 3: Financials (auto-populated from Financials tab) */}
      <Card><CardContent className="pt-5">
        <h3 className="text-xs font-semibold text-blue-800 uppercase tracking-wider mb-3">Financials</h3>
        <p className="text-xs text-gray-400 mb-3">Auto-populated from project Financials & Subcontractors</p>
        <div className="mb-3">
          <CInput label="Total Receivable (Revised Contract Value)" value={form.total_receivable} onChange={v => !readOnly && set('total_receivable', v)} testId="cs-receivable" readOnly={readOnly} />
        </div>
        {vps.length > 0 && (
          <div className="space-y-2 mb-3">
            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Vendor / Sub Payables</h4>
            <div className="bg-slate-50 rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">Vendor / Sub</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-slate-600">BCS PO #</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-slate-600">Payable Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {vps.map((vp, idx) => (
                    <tr key={idx} className="border-t border-slate-200">
                      <td className="px-3 py-2 font-medium text-slate-800">{vp.name}{vp.trade ? <span className="text-xs text-slate-400 ml-1">({vp.trade})</span> : ''}</td>
                      <td className="px-3 py-2 text-xs font-mono text-slate-500">{vp.bcs_po_number || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-red-600">{fmt(vp.payable_amount)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300 bg-slate-100">
                    <td colSpan={2} className="px-3 py-2 font-semibold text-slate-700 text-right">Subtotal Vendor Payables:</td>
                    <td className="px-3 py-2 text-right font-bold text-red-600">{fmt(vendorTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CInput label="Misc Payable" value={form.misc_payable} onChange={v => !readOnly && set('misc_payable', v)} testId="cs-misc" />
          {(form.misc_payable > 0) && (
            <TInput label="Misc Description" value={form.misc_payable_description} onChange={v => set('misc_payable_description', v)} placeholder="What is this for?" className="md:col-span-3" readOnly={readOnly} />
          )}
        </div>
        <div className={`mt-4 p-4 rounded-lg text-center ${netProfit >= 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
          <p className={`text-xs font-semibold uppercase tracking-wider ${netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Net Profit</p>
          <p className={`text-3xl font-bold mt-1 ${netProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="cs-net-profit">{fmt(netProfit)}</p>
          <p className="text-xs text-gray-500 mt-1">Total Receivable - Vendor Payables ({vps.length}) - Misc</p>
        </div>
      </CardContent></Card>

      {/* Section 4: Attachments — Shows documents from Documents tab */}
      <Card><CardContent className="pt-5">
        <h3 className="text-xs font-semibold text-blue-800 uppercase tracking-wider mb-3">Attachments & Documents</h3>
        {allDocs.length > 0 ? (
          <div className="space-y-3">
            {allDocs.map((doc, idx) => {
              const noteVal = (form.attachment_notes || []).find(n => n.file_id === doc.id)?.note || '';
              return (
                <div key={doc.id || idx} className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText size={16} className="text-red-500 flex-shrink-0" />
                      <button onClick={() => window.open(fileUrl(doc.serve_path), '_blank')} className="text-sm font-medium text-blue-600 hover:underline truncate" title={doc.filename}>
                        {doc.filename}
                      </button>
                      <span className="text-xs text-gray-400">{(doc.size/1024).toFixed(0)} KB</span>
                      {doc.category && <Badge className="text-[10px] bg-slate-100 text-slate-600">{doc.category.replace('_', ' ')}</Badge>}
                    </div>
                    <Button variant="outline" size="sm" className="flex-shrink-0" onClick={() => window.open(fileUrl(doc.serve_path), '_blank')}>
                      <ExternalLink size={12} className="mr-1" /> View
                    </Button>
                  </div>
                  {!readOnly && (
                    <div>
                      <input placeholder="Add a note for this attachment..."
                        value={noteVal}
                        onChange={e => {
                          const notes = [...(form.attachment_notes || [])];
                          const existing = notes.findIndex(n => n.file_id === doc.id);
                          if (existing >= 0) { notes[existing] = { ...notes[existing], note: e.target.value }; }
                          else { notes.push({ file_id: doc.id, filename: doc.filename, note: e.target.value }); }
                          set('attachment_notes', notes);
                        }}
                        className="w-full px-2 py-1.5 border rounded text-sm text-gray-600" />
                    </div>
                  )}
                  {readOnly && noteVal && <p className="text-xs text-gray-500 mt-1"><StickyNote size={10} className="inline mr-1" />{noteVal}</p>}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-400">No documents uploaded. Upload files in the Documents tab.</p>
        )}
      </CardContent></Card>

      {/* Section 5: Notes (multiple) */}
      <Card><CardContent className="pt-5">
        <h3 className="text-xs font-semibold text-blue-800 uppercase tracking-wider mb-3">Notes</h3>
        <div className="space-y-2">
          {(form.notes || []).map((note, idx) => (
            <div key={idx} className="flex gap-2">
              <textarea value={note.text || ''} disabled={readOnly}
                onChange={e => {
                  const notes = [...(form.notes || [])];
                  notes[idx] = { ...notes[idx], text: e.target.value };
                  set('notes', notes);
                }}
                rows={2} className="flex-1 px-3 py-2 border rounded-lg text-sm" placeholder="Add a note..." />
              {!readOnly && (form.notes || []).length > 1 && (
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0 text-red-400 flex-shrink-0"
                  onClick={() => set('notes', (form.notes || []).filter((_, i) => i !== idx))}>
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          ))}
          {!readOnly && (
            <Button variant="outline" size="sm" onClick={() => set('notes', [...(form.notes || []), { text: '', created_at: new Date().toISOString() }])}>
              <Plus size={14} className="mr-1" /> Add Note
            </Button>
          )}
        </div>
        <div className="mt-3">
          <Label className="text-xs text-slate-500">Internal Notes</Label>
          <textarea value={form.internal_notes || ''} disabled={readOnly}
            onChange={e => set('internal_notes', e.target.value)}
            rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Internal notes, reminders..." />
        </div>
      </CardContent></Card>
    </div>
  );
};

// ═══════════════════════════════════════════
// COVERSHEET LIST (Dashboard)
// ═══════════════════════════════════════════
const InvoiceCoversheetTab = ({ projectId, project }) => {
  const [coversheets, setCoversheets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewId, setViewId] = useState(null);
  const [viewReadOnly, setViewReadOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  const [projectContext, setProjectContext] = useState(null);

  const loadContext = useCallback(async () => {
    try {
      const [finRes, subRes, custRes, invRes, projRes] = await Promise.all([
        fetch(`${API}/api/crm/projects/${projectId}/financials`, { credentials: 'include' }),
        fetch(`${API}/api/crm/projects/${projectId}/subcontractors`, { credentials: 'include' }),
        fetch(`${API}/api/crm/projects/${projectId}/customer-info`, { credentials: 'include' }),
        fetch(`${API}/api/crm/projects/${projectId}/invoices`, { credentials: 'include' }),
        fetch(`${API}/api/crm/projects/${projectId}`, { credentials: 'include' }),
      ]);
      const fin = await finRes.json();
      const subs = (await subRes.json()).assignments || [];
      const customerInfo = custRes.ok ? await custRes.json() : {};
      const invoices = (await invRes.json()).invoices || [];
      const projData = await projRes.json();
      const docs = {
        bcs_quote: projData.bcs_quote || null,
        sub_quotes: projData.sub_quotes || [],
        customer_po: projData.customer_po || null,
        vendor_invoices: projData.vendor_invoices || [],
        paid_invoices: projData.paid_invoices || [],
      };
      setProjectContext({ fin, subs, customerInfo, invoices, docs });
    } catch { /* ignore */ }
  }, [projectId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const [csRes, stRes] = await Promise.all([
        fetch(`${API}/api/crm/projects/${projectId}/coversheets?${params}`, { credentials: 'include' }),
        fetch(`${API}/api/crm/projects/${projectId}/coversheets/stats`, { credentials: 'include' }),
      ]);
      setCoversheets((await csRes.json()).coversheets || []);
      setStats(await stRes.json());
    } catch { toast.error('Failed to load coversheets'); }
    finally { setLoading(false); }
  }, [projectId, search, statusFilter]);

  useEffect(() => { load(); loadContext(); }, [load, loadContext]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this coversheet?')) return;
    await fetch(`${API}/api/crm/projects/${projectId}/coversheets/${id}`, { credentials: 'include', method: 'DELETE' });
    toast.success('Deleted');
    load();
  };

  const openView = (e, id) => {
    e.stopPropagation();
    setViewReadOnly(true);
    setViewId(id);
  };

  if (viewId || creating) {
    return <CoversheetForm projectId={projectId} coversheetId={viewId || null}
      onBack={() => { setViewId(null); setViewReadOnly(false); setCreating(false); load(); }}
      project={project} projectContext={projectContext} readOnly={viewReadOnly} />;
  }

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card data-testid="cs-stat-total"><CardContent className="pt-4 pb-3 text-center">
            <FileText className="mx-auto mb-1 text-blue-600" size={18} />
            <p className="text-lg font-bold text-slate-900">{stats.total}</p>
            <p className="text-[10px] text-gray-500">Total Coversheets</p>
          </CardContent></Card>
          <Card data-testid="cs-stat-receivable"><CardContent className="pt-4 pb-3 text-center">
            <DollarSign className="mx-auto mb-1 text-emerald-600" size={18} />
            <p className="text-lg font-bold text-emerald-700">{fmt(stats.total_receivable)}</p>
            <p className="text-[10px] text-gray-500">Total Receivable</p>
          </CardContent></Card>
          <Card data-testid="cs-stat-profit"><CardContent className="pt-4 pb-3 text-center">
            <CheckCircle className="mx-auto mb-1 text-emerald-600" size={18} />
            <p className={`text-lg font-bold ${stats.total_net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(stats.total_net_profit)}</p>
            <p className="text-[10px] text-gray-500">Total Net Profit</p>
          </CardContent></Card>
          <Card data-testid="cs-stat-payables"><CardContent className="pt-4 pb-3 text-center">
            <AlertCircle className="mx-auto mb-1 text-amber-600" size={18} />
            <p className="text-lg font-bold text-amber-700">{fmt(stats.total_payables)}</p>
            <p className="text-[10px] text-gray-500">Total Payables</p>
          </CardContent></Card>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex gap-2 flex-1 min-w-0">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-2.5 text-gray-400" size={14} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customer, PO#, invoice#..."
              className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm" data-testid="cs-search" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white" data-testid="cs-status-filter">
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={() => setCreating(true)} data-testid="new-coversheet-btn">
          <Plus size={14} className="mr-1" /> New Coversheet
        </Button>
      </div>

      {loading ? <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>
      : coversheets.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-400">
          <FileText className="mx-auto mb-2" size={32} />
          <p>No invoice coversheets yet</p>
          <p className="text-xs mt-1">Click "New Coversheet" to create one</p>
        </CardContent></Card>
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Customer</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Customer PO #</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">BCS Invoice #</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Receivable</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Net Profit</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coversheets.map(cs => (
                <tr key={cs.id} className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                  onClick={() => { setViewReadOnly(false); setViewId(cs.id); }}
                  data-testid={`cs-row-${cs.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{cs.customer_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs">{cs.customer_po_number || cs.po_number || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs">{cs.bcs_invoice_number || cs.invoice_number || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{cs.invoice_date || '—'}</td>
                  <td className="px-4 py-3"><Badge className={`text-xs ${STATUS_COLORS[cs.status] || 'bg-gray-100'}`}>{cs.status}</Badge></td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-800">{fmt(cs.total_receivable)}</td>
                  <td className={`px-4 py-3 text-right font-semibold ${(cs.net_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(cs.net_profit)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-500" onClick={(e) => openView(e, cs.id)} data-testid={`cs-view-${cs.id}`}>
                        <Eye size={13} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={(e) => handleDelete(e, cs.id)} data-testid={`cs-delete-${cs.id}`}>
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoiceCoversheetTab;
